import React, { useEffect, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMailBulk } from "@fortawesome/free-solid-svg-icons";
import { faGithub, faLinkedin } from "@fortawesome/free-brands-svg-icons";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { TextPlugin } from "gsap/TextPlugin";
import Lenis from "lenis";

import NavBar from "../components/common/navBar";
import Footer from "../components/common/footer";
import Works from "../components/homepage/works";
import AllProjects from "../components/projects/allProjects";
import CredlyBadge from "../components/common/CredlyBadge";
import WebGLBackground from "../components/homepage/WebGLBackground";

import INFO from "../data/user";
import SEO from "../data/seo";
import "./styles/homepage.css";

gsap.registerPlugin(ScrollTrigger, TextPlugin);

const SKILLS = [
	'React.js', 'Node.js', 'Flutter', 'Laravel', 'Next.js', '.NET APIs',
	'CRM Dev', 'AI Integration', 'AWS', 'MongoDB', 'PostgreSQL', 'n8n',
	'Zapier', 'React Native', 'Python', 'TypeScript', 'Docker', 'Web3',
	'Shopify', 'WhatsApp API', 'Angular', 'PHP', 'Java', 'Tailwind CSS',
];

const Homepage = () => {
	const heroRef     = useRef(null);
	const titleRef    = useRef(null);
	const subtitleRef = useRef(null);
	const socialsRef  = useRef(null);
	const imageRef    = useRef(null);
	const certRef     = useRef(null);
	const projectsRef = useRef(null);
	const worksRef    = useRef(null);
	const marqueeRef  = useRef(null);

	const currentSEO = SEO.find((item) => item.page === "home");

	useEffect(() => {
		window.scrollTo(0, 0);

		const lenis = new Lenis({
			duration: 1.2,
			easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
			smoothWheel: true,
		});
		lenis.on("scroll", ScrollTrigger.update);
		const raf = (time) => { lenis.raf(time); requestAnimationFrame(raf); };
		requestAnimationFrame(raf);

		// ── Hero entrance ──
		const tl = gsap.timeline({ defaults: { ease: "power3.out" } });
		tl.fromTo(titleRef.current,    { y: 70, opacity: 0 }, { y: 0, opacity: 1, duration: 1 })
		  .fromTo(subtitleRef.current, { y: 40, opacity: 0 }, { y: 0, opacity: 1, duration: 0.9 }, "-=0.6")
		  .fromTo(socialsRef.current,  { y: 20, opacity: 0 }, { y: 0, opacity: 1, duration: 0.7 }, "-=0.5")
		  .fromTo(imageRef.current,    { x: 60, opacity: 0, scale: 0.95 }, { x: 0, opacity: 1, scale: 1, duration: 1.1 }, "-=1.2");

		// ── Marquee infinite scroll ──
		if (marqueeRef.current) {
			gsap.to(marqueeRef.current, {
				xPercent: -50,
				duration: 25,
				ease: 'none',
				repeat: -1,
			});
		}

		// ── Scroll reveals with stagger ──
		[certRef, projectsRef, worksRef].forEach((ref) => {
			if (!ref.current) return;
			gsap.fromTo(ref.current,
				{ y: 60, opacity: 0 },
				{ y: 0, opacity: 1, duration: 1, ease: "power2.out",
				  scrollTrigger: { trigger: ref.current, start: "top 85%", toggleActions: "play none none none" } }
			);
		});

		// ── Stagger cards on scroll ──
		ScrollTrigger.batch('.project', {
			onEnter: (els) => gsap.fromTo(els,
				{ y: 50, opacity: 0 },
				{ y: 0, opacity: 1, duration: 0.7, stagger: 0.1, ease: 'power2.out' }
			),
			start: 'top 88%',
		});

		// ── Work items stagger ──
		ScrollTrigger.batch('.work', {
			onEnter: (els) => gsap.fromTo(els,
				{ x: -30, opacity: 0 },
				{ x: 0, opacity: 1, duration: 0.5, stagger: 0.08, ease: 'power2.out' }
			),
			start: 'top 90%',
		});

		return () => {
			lenis.destroy();
			ScrollTrigger.getAll().forEach((t) => t.kill());
		};
	}, []);

	const doubled = [...SKILLS, ...SKILLS];

	return (
		<React.Fragment>
			<Helmet>
				<title>{INFO.main.title}</title>
				<meta name="description" content={currentSEO.description} />
				<meta name="keywords" content={currentSEO.keywords.join(", ")} />
			</Helmet>

			<div className="page-content">
				<WebGLBackground />
				<NavBar active="home" />

				<div className="content-wrapper">

					{/* ── Hero ── */}
					<section className="hero" ref={heroRef}>
						<div className="hero-left">
							<div className="hero-badge">
								<span className="hero-badge-dot" />
								Available · UAE · Remote · Project-Based
							</div>
							<h1 className="title hero-title" ref={titleRef}>
								{INFO.homepage.title.split("\n").map((line, i) => (
									<span key={i} className={i === 1 ? "hero-title-accent" : ""}>{line}<br /></span>
								))}
							</h1>
							<p className="subtitle hero-subtitle" ref={subtitleRef}>
								{INFO.homepage.description.split("**").map((part, i) =>
									i % 2 === 1 ? <strong key={i}>{part}</strong> : part
								)}
							</p>
							<div className="hero-socials" ref={socialsRef}>
								<a href={INFO.socials.linkedin} target="_blank" rel="noreferrer" className="social-btn">
									<FontAwesomeIcon icon={faLinkedin} /> LinkedIn
								</a>
								<a href={INFO.socials.github} target="_blank" rel="noreferrer" className="social-btn">
									<FontAwesomeIcon icon={faGithub} /> GitHub
								</a>
								<a href={`mailto:${INFO.main.email}`} className="social-btn social-btn-outline">
									<FontAwesomeIcon icon={faMailBulk} /> Email Me
								</a>
							</div>
							<div className="hero-cert-pill">
								<img src="google-data-analytics-professional-certificate.png" alt="Google Certificate" />
								<span>Google Data Analytics Professional</span>
							</div>
						</div>
						<div className="hero-right" ref={imageRef}>
							<div className="hero-image-frame">
								<img src="homepage.png" alt="Hamdan" className="hero-image" />
								<div className="hero-image-glow" />
							</div>
							<div className="hero-stats">
								<div className="hero-stat">
									<span className="hero-stat-num">4+</span>
									<span className="hero-stat-label">Years Exp.</span>
								</div>
								<div className="hero-stat-divider" />
								<div className="hero-stat">
									<span className="hero-stat-num">20+</span>
									<span className="hero-stat-label">Projects</span>
								</div>
								<div className="hero-stat-divider" />
								<div className="hero-stat">
									<span className="hero-stat-num">UAE</span>
									<span className="hero-stat-label">Based</span>
								</div>
							</div>
						</div>
					</section>

					{/* ── Skills Marquee ── */}
					<div className="marquee-wrap">
						<div className="marquee-track" ref={marqueeRef}>
							{doubled.map((s, i) => (
								<span key={i} className="marquee-item">
									<span className="marquee-dot" />{ s }
								</span>
							))}
						</div>
					</div>

					{/* ── Certifications ── */}
					<section className="section-block" ref={certRef}>
						<div className="section-label">Certifications</div>
						<div className="certs-grid">
							<CredlyBadge shareBadgeId="aa848900-9cae-489e-ab62-1d5683a98709" iframeWidth={200} iframeHeight={270} />
							<CredlyBadge shareBadgeId="aa4839d9-c429-4b02-991b-d1cac7b9ffc8" iframeWidth={200} iframeHeight={270} />
							<CredlyBadge shareBadgeId="b0c72e7c-30f8-4191-8ec2-ed558c15295e" iframeWidth={200} iframeHeight={270} />
						</div>
						<a href="/certifications" className="section-link">View all certifications →</a>
					</section>

					{/* ── Projects ── */}
					<section className="section-block" ref={projectsRef}>
						<div className="section-label">Projects</div>
						<AllProjects />
					</section>

					{/* ── Work Experience ── */}
					<section className="section-block" ref={worksRef}>
						<div className="section-label">Experience</div>
						<Works />
					</section>

					<div className="page-footer">
						<Footer />
					</div>
				</div>
			</div>
		</React.Fragment>
	);
};

export default Homepage;
